import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordenesApi, proyectosApi } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { Loader2, ExternalLink } from 'lucide-react';

const ESTADOS = [
  { value: 'EN EJECUCION', label: 'En Ejecución' },
  { value: 'TERMINADA', label: 'Terminada' },
];
const ESTADOS_FIN = [
  { value: 'ACTIVA', label: 'Activa' },
  { value: 'CERRADA', label: 'Cerrada' },
];
const AREAS = ['FABRICACION', 'MONTAJE', 'OBRAS CIVILES', 'RILES', 'ARRIENDO MAQ', 'TRANSPORTE', 'NO PRODUCTIVO'];

const selectCls = 'flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-ring';

export const OTDatosModal = ({ otId, open, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ot, setOt] = useState(null);
  const [form, setForm] = useState(null);
  const [proyectos, setProyectos] = useState([]);

  useEffect(() => {
    if (!open || !otId) return;
    setLoading(true);
    setForm(null);
    Promise.all([ordenesApi.getOne(otId), proyectosApi.getAll()]).then(([res, resP]) => {
      const d = res.data;
      const lista = resP.data || [];
      setProyectos(lista);
      let estado = d.estado;
      if (['COMPLETADA', 'CERRADA', 'PENDIENTE', 'DETENIDA'].includes(estado)) estado = 'TERMINADA';
      let fin = (d.estfinan === false || d.estfinan === 'NO') ? 'CERRADA' : 'ACTIVA';
      if (estado === 'EN EJECUCION') fin = 'ACTIVA';
      let proyectoId = d.proyecto_id || '';
      if (!proyectoId && d.proyecto_codigo) {
        const match = lista.find(p => String(p.codigo) === String(d.proyecto_codigo));
        if (match) proyectoId = match.id;
      }
      setOt(d);
      setForm({
        descripcion: d.descripcion || '', estado, estado_financiero: fin,
        proyecto_id: proyectoId,
        area: d.area || 'FABRICACION', responsable: d.responsable || '',
        contacto_cliente: d.contacto_cliente || '',
        fecha_inicio: d.fecha_inicio || '', fecha_termino: d.fecha_termino || '',
        cotizacion: d.cotizacion || '', orden_compra: d.orden_compra || '',
        monto: d.monto ?? 0, avance: d.avance ?? 0,
        garantia: !!d.garantia, observacion: d.observacion || '',
      });
    }).catch(() => { toast.error('No se pudo cargar la OT'); onClose(false); })
      .finally(() => setLoading(false));
  }, [open, otId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    if (k === 'estado' && v === 'EN EJECUCION') next.estado_financiero = 'ACTIVA';
    return next;
  });

  const handleSave = async () => {
    if (!form.descripcion.trim()) { toast.error('La descripción es obligatoria'); return; }
    setSaving(true);
    try {
      await ordenesApi.update(otId, {
        descripcion: form.descripcion,
        estado: form.estado,
        estfinan: form.estado_financiero === 'ACTIVA',
        ...(form.proyecto_id ? { proyecto_id: form.proyecto_id } : {}),
        area: form.area,
        responsable: form.responsable || null,
        contacto_cliente: form.contacto_cliente || null,
        fecha_inicio: form.fecha_inicio || null,
        fecha_termino: form.fecha_termino || null,
        cotizacion: form.cotizacion || null,
        orden_compra: form.orden_compra || null,
        monto: parseFloat(form.monto) || 0,
        avance: Math.max(0, Math.min(100, parseFloat(form.avance) || 0)),
        garantia: form.garantia,
        observacion: form.observacion || null,
      });
      if (form.estado_financiero === 'CERRADA') {
        toast.success(`OT ${ot.numero_ot} cerrada financieramente — saldrá del informe`);
      } else {
        toast.success(`OT ${ot.numero_ot} actualizada`);
      }
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(false); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ot-datos-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between text-base">
            <span>Datos de la OT {ot?.numero_ot ? `— OT ${ot.numero_ot}` : ''}</span>
            {ot?.id && (
              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 mr-4" onClick={() => navigate(`/ordenes-trabajo/${ot.id}/editar`)} data-testid="btn-ficha-completa">
                <ExternalLink className="w-3 h-3" /> Ficha completa
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading || !form ? (
          <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
        ) : (
          <div className="space-y-3 text-xs">
            <div className="grid grid-cols-3 gap-2 bg-muted/40 rounded p-2 items-center">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Proyecto (reasignable)</Label>
                <select value={form.proyecto_id} onChange={(e) => set('proyecto_id', e.target.value)} className={selectCls} data-testid="modal-proyecto">
                  {!form.proyecto_id && <option value="">— {ot.proyecto_codigo || 'Sin proyecto'} —</option>}
                  {proyectos.map(p => (
                    <option key={p.id} value={p.id}>{p.codigo} — {(p.descripcion || '').slice(0, 45)}{p.estado === 'CERRADO' ? ' [CERRADO]' : ''}</option>
                  ))}
                </select>
              </div>
              <div className="truncate pt-4"><span className="text-muted-foreground">Cliente:</span> <b>{ot.cliente_nombre || '—'}</b></div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Descripción *</Label>
              <Textarea value={form.descripcion} onChange={(e) => set('descripcion', e.target.value)} rows={2} className="text-xs" data-testid="modal-descripcion" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Estado</Label>
                <select value={form.estado} onChange={(e) => set('estado', e.target.value)} className={selectCls} data-testid="modal-estado">
                  {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Estado Financiero</Label>
                <select value={form.estado_financiero} onChange={(e) => set('estado_financiero', e.target.value)}
                  disabled={form.estado === 'EN EJECUCION'}
                  className={`${selectCls} ${form.estado === 'EN EJECUCION' ? 'bg-muted cursor-not-allowed' : ''}`}
                  data-testid="modal-estado-financiero">
                  {ESTADOS_FIN.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                </select>
                {form.estado === 'EN EJECUCION' && <p className="text-[10px] text-muted-foreground">En ejecución siempre está Activa</p>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Área</Label>
                <select value={form.area} onChange={(e) => set('area', e.target.value)} className={selectCls} data-testid="modal-area">
                  {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Avance %</Label>
                <Input type="number" min="0" max="100" value={form.avance} onChange={(e) => set('avance', e.target.value)} className="h-8 text-xs" data-testid="modal-avance" />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Monto (neto)</Label>
                <Input type="number" value={form.monto} onChange={(e) => set('monto', e.target.value)} className="h-8 text-xs" data-testid="modal-monto" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Cotización</Label>
                <Input value={form.cotizacion} onChange={(e) => set('cotizacion', e.target.value)} className="h-8 text-xs" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Orden de Compra</Label>
                <Input value={form.orden_compra} onChange={(e) => set('orden_compra', e.target.value)} className="h-8 text-xs" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Responsable</Label>
                <Input value={form.responsable} onChange={(e) => set('responsable', e.target.value)} className="h-8 text-xs" />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Fecha inicio</Label>
                <Input type="date" value={form.fecha_inicio} onChange={(e) => set('fecha_inicio', e.target.value)} className="h-8 text-xs" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Fecha término</Label>
                <Input type="date" value={form.fecha_termino} onChange={(e) => set('fecha_termino', e.target.value)} className="h-8 text-xs" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Contacto cliente</Label>
                <Input value={form.contacto_cliente} onChange={(e) => set('contacto_cliente', e.target.value)} className="h-8 text-xs" />
              </div>
              <div className="flex items-end gap-2 pb-1">
                <Checkbox checked={form.garantia} onCheckedChange={(v) => set('garantia', !!v)} id="modal-garantia" data-testid="modal-garantia" />
                <Label htmlFor="modal-garantia" className="text-xs cursor-pointer">Garantía</Label>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Observación</Label>
              <Textarea value={form.observacion} onChange={(e) => set('observacion', e.target.value)} rows={2} className="text-xs" />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => onClose(false)} disabled={saving} data-testid="btn-cancelar-ot-modal">Cancelar</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="btn-guardar-ot-modal">
                {saving && <Loader2 className="w-3 h-3 animate-spin mr-1" />} Guardar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
